/**
 * Platform Admin API Client
 * 
 * API calls for getbitsy.ai platform administrators (not hotel-specific)
 */
import { BACKEND_URL, API } from '@/lib/backendUrl';

const API_BASE_URL = BACKEND_URL || '';

/**
 * Chat with Hermes agent
 */
export const chatWithHermes = async (message, context = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/agents/platform/hermes/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      context
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to chat with Hermes');
  }
  return response.json();
};

/**
 * Get all enrolled hotels
 */
export const getAllHotels = async () => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/hotels`);
  if (!response.ok) {
    throw new Error('Failed to fetch hotels');
  }
  return response.json();
};

/**
 * Get platform metrics
 */
export const getPlatformMetrics = async () => {
  // Use Hermes to get metrics
  const response = await chatWithHermes('Get platform metrics');
  return response;
};

/**
 * Check system health
 */
export const checkSystemHealth = async () => {
  // Use Hermes to check health
  const response = await chatWithHermes('Check system health');
  return response;
};

/**
 * Get hotel details
 */
export const getHotelDetails = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/hotels/${hotelId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch hotel details');
  }
  return response.json();
};

/**
 * Register new hotel
 */
export const registerHotel = async (hotelData) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/register-hotel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(hotelData),
  });
  
  if (!response.ok) {
    throw new Error('Failed to register hotel');
  }
  return response.json();
};

/**
 * Get scoring data for a hotel
 */
export const getHotelScore = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/scoring/hotel/${hotelId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch hotel score');
  }
  return response.json();
};

/**
 * Calculate score for a hotel
 */
export const calculateHotelScore = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/scoring/calculate/${hotelId}`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    throw new Error('Failed to calculate hotel score');
  }
  return response.json();
};

/**
 * Get staking status for a hotel
 */
export const getStakingStatus = async (hotelId) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/staking/status/${hotelId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch staking status');
  }
  return response.json();
};

/**
 * Get all blockchain events
 */
export const getBlockchainEvents = async (limit = 50) => {
  const response = await fetch(`${API_BASE_URL}/api/blockchain/events?limit=${limit}`);
  if (!response.ok) {
    throw new Error('Failed to fetch blockchain events');
  }
  return response.json();
};

/**
 * Get agent telemetry
 */
export const getAgentTelemetry = async (hotelId = null, agentName = null, limit = 50) => {
  let url = `${API_BASE_URL}/api/blockchain/agents/telemetry?limit=${limit}`;
  if (hotelId) url += `&hotel_id=${hotelId}`;
  if (agentName) url += `&agent_name=${agentName}`;
  
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch agent telemetry');
  }
  return response.json();
};
